import React, {
	useState,
	useEffect,
	useRef,
	useMemo,
	useCallback,
} from "react";
import classes from "./index.module.css";
import { AgGridReact } from "ag-grid-react";
import DefaultSelect from "../../components/form/DefaultSelect";
import { ReactComponent as RegisterIcon } from "../../assets/add-plus.svg";
import { ReactComponent as ExportIcon } from "../../assets/arrow-download-small.svg";
import { ReactComponent as SearchIcon } from "../../assets/search-find.svg";
import DefaultInput from "../../components/form/DefaultInput";
import { Button } from "antd";
import { ColDef } from "ag-grid-community";
import OrderStatus from "../../components/OrderStatus";
import PaginationTable from "../../components/Pagination";
import FinanceStatistic from "../../components/FinanceStatistic";
import { exportDataToCsv } from "../../utils/exportDataToCsv";
import api from "../../utils/axios";
import { valueDateFormatter } from "../../utils/formatters";
import DefaultDatePicker from "../../components/form/DefaultDatePicker";
import useLang from "../../hooks/useLang";
import { navigateToClientDetails } from "../../utils/navigateToClientDetails";
import { useMediaQuery } from "@mui/material";

const BlackListAccounts = () => {
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [code, setCode] = useState<string | undefined>();
	const [tableData, setTableData] = useState<any[]>([]);
	let grid = useRef(null);
	const [fromDate, setFromDate] = useState<Date | null>(null);
	const [toDate, setToDate] = useState<Date | null>(null);
	const { strings } = useLang("blackListAccounts");
	const nameAndSurnameGetter = (params: any) => {
		const text = params.data.firstName + " " + params.data.lastName;
		return text;
	};

	const fetchPassiveAccounts = useCallback(async () => {
		try {
			const response = await api.get("User/fetch-passive-accounts");
			if (response.status === 200) {
				const results = response?.data;
				setTableData(results);
			}
		} catch (error) {
			console.log(error);
		}
	}, []);

	useEffect(() => {
		if (!strings) {
			return;
		}
		fetchPassiveAccounts();
		setTableColumns([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 50,
			},
			{
				field: "id",
				headerName: strings?.headers.headerNameId,
				cellStyle: (params: any) => ({
					fontSize: "15px",
					color: "blue",
				}),
				width: 80,
			},
			{
				field: "firstName",
				valueGetter: nameAndSurnameGetter,
				headerName: strings?.headers.headerNameFirstLastName,
				width: 130,
			},
			{
				field: "phone",
				headerName: strings?.headers.headerNamePhone,
				width: 150,
			},
			{
				field: "businessName",
				headerName: strings?.headers.headerNameBusinessName,
				width: 130,
			},
			{
				field: "businessNr",
				headerName: strings?.headers.headerNameBusinessNr,
				width: 130,
			},
			{
				field: "orderNumber",
				headerName: strings?.headers.headerNameOrderNumber,
				width: 120,
			},
			{
				field: "latestOrderDate",
				headerName: strings?.headers.headerNameLatestOrderDate,
				// width: 150,
			},
		]);
	}, [setTableColumns, strings]);

	const handlePageChange = (page: any) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (current: number, size: number) => {
		setPageSize(size);
		setCurrentPage(1);
	};

	const startIndex = (currentPage - 1) * pageSize;
	const endIndex = startIndex + pageSize;

	const filteredData = useMemo(() => {
		return tableData.filter((item: any) => {
			const itemDate = new Date(item.latestOrderDate);
			const fromDateObj = fromDate ? new Date(fromDate) : null;
			let toDateObj = toDate ? new Date(toDate) : null;

			return (
				(!fromDateObj || itemDate >= fromDateObj) &&
				(!toDateObj || itemDate <= toDateObj) &&
				(!code ||
					(item.firstName &&
						item.firstName
							?.toLowerCase()
							.includes(code?.toLowerCase())) ||
					(item.lastName &&
						item.lastName
							?.toLowerCase()
							.includes(code?.toLowerCase())) ||
					(item.id &&
						item.id
							.toString()
							?.toLowerCase()
							.includes(code?.toLowerCase())))
			);
		});
	}, [code, fromDate, tableData, toDate]);

	const paginatedBlacklistAccounts = useMemo(() => {
		const dataToPaginate = filteredData;
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return dataToPaginate.slice(startIndex, endIndex);
	}, [currentPage, filteredData, pageSize]);

	const selectedColumnIds = [
		"id",
		"firstName",
		"phone",
		"businessName",
		"businessNr",
		"orderNumber",
		"latestOrderDate",
	];

	const onExportSelectedId = () => {
		const dataToExport = filteredData.map((item) => ({
			id: item.id,
			firstName: `${item.firstName} ${item.lastName}`,
			phone: item.phone,
			businessName: item.businessName,
			businessNr: item.businessNr,
			orderNumber: item.orderNumber,
			latestOrderDate: item.latestOrderDate,
		}));
		exportDataToCsv(grid, selectedColumnIds, dataToExport);
	};

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "id":
				await navigateToClientDetails(params?.data?.id);
				break;
		}
	};

	const onFirstDataRendered = useCallback((params: any) => {
		(grid.current as any).api.sizeColumnsToFit();
	}, []);

	return (
		<div className={classes.mainContainer}>
			<div className={classes.heroContainer}>
				<div className={classes.firstContainer}>
					<h1 className={classes.title}>
						{strings?.headers?.title} &nbsp;
						<span className={classes.clientsCount}>
							({tableData.length})
						</span>
					</h1>
					<div className={classes.buttons}>
						<Button
							className={classes.button}
							onClick={onExportSelectedId}
						>
							<ExportIcon />
							{strings?.buttonText}
						</Button>
					</div>
				</div>
				<div className={classes.inputs}>
					<DefaultInput
						label={strings?.inputPlaceholders?.search}
						className={classes.input}
						placeholder={strings?.inputPlaceholders?.search}
						prefix={<SearchIcon />}
						value={code}
						onChange={(e: any) => {
							setCurrentPage(1);
							setCode(e.target.value);
						}}
						allowClear
					/>
					<div className="flex flex-wrap  gap-10">
						<DefaultDatePicker
							label={strings?.datePickerLabels?.from}
							value={fromDate}
							onChange={(date: any) => {
								setCurrentPage(1);
								setFromDate(date);
							}}
							className={classes.input}
						/>
						<DefaultDatePicker
							label={strings?.datePickerLabels?.to}
							value={toDate}
							onChange={(date: any) => {
								setCurrentPage(1);
								setToDate(date);
							}}
							className={classes.input}
						/>
					</div>
				</div>
				<div className={classes.pagination}>
					<PaginationTable
						current={currentPage}
						pageSize={pageSize}
						total={tableData.length}
						startOrderIndex={startIndex}
						endOrderIndex={endIndex}
						onChange={handlePageChange}
						onShowSizeChange={handlePageSizeChange}
					/>
				</div>
				<div
					className="ag-theme-alpine"
					style={{
						height: 600,
						width: "100%",
					}}
				>
					<AgGridReact
						// rowData={filteredData}
						rowData={paginatedBlacklistAccounts}
						columnDefs={tableColumns}
						undoRedoCellEditing={true}
						undoRedoCellEditingLimit={20}
						suppressCopyRowsToClipboard={true}
						rowSelection="multiple"
						ref={grid}
						enableRangeSelection={true}
						cacheQuickFilter={true}
						onCellClicked={onCellClicked}
						defaultColDef={defaultColDef}
						defaultExcelExportParams={defaultExcelExportSettings}
						onFirstDataRendered={onFirstDataRendered}
					/>
				</div>
			</div>
		</div>
	);
};

export default BlackListAccounts;
